import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { fetchAuthSession } from "aws-amplify/auth";
import "dayjs/locale/en-gb";
import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { Article } from "./components/pages/Article";
import { ArticleEReaderView } from "./components/pages/ArticleEReaderView";
import { FrjArticles } from "./components/pages/FrjArticles";
import { FrjTagsListPage } from "./components/pages/FrjTagsListPage";
import { FrjTagArticlesPage } from "./components/pages/FrjTagArticlesPage";
import { FrjIssuePage } from "./components/pages/FrjIssuePage";
import { AnnotationProvider } from "./components/contexts/WithAnnotations/AnnotationProvider";
import { ArticleAnnotationProvider } from "./components/contexts/WithAnnotations/ArticleAnnotationProvider";
import { ArticleBookmarkProvider } from "./components/contexts/WithBookmarks/ArticleBookmarkProvider";
import { CustomAuthenticator } from "./components/contexts/WithAuth/auth/authenticator.tsx";
import ProtectedAdminRoute from "./components/contexts/WithAuth/ProtectedAdminRoute";
import { UserProvider } from "./components/contexts/WithAuth/UserProvider";
import { PublicationProvider } from "./components/contexts/WithPublication/PublicationProvider";
import { ArticleProvider } from "./components/contexts/WithArticle/ArticleProvider";
import { UserPublicationsProvider } from "./components/contexts/WithPublications/UserPublicationsProvider";
import { SearchProvider } from "./components/contexts/WithSearch/SearchProvider";
import { SettingsProvider } from "./components/contexts/WithSettings/SettingsProvider";
import { SidebarProvider } from "./components/contexts/WithSidebars/SidebarsProvider";
import { useConfig } from "./components/hooks/useConfig";
import { OrganisationAdmin } from "./components/pages/admin/OrganisationAdmin";
import { PublicationAdmin } from "./components/pages/admin/PublicationAdmin";
import { LoginPageAdmin } from "./components/pages/admin/LoginPageAdmin";
import { SearchSuggestionsAdmin } from "./components/pages/admin/SearchSuggestions";
import { FrjTagsFilterAdmin } from "./components/pages/admin/FrjTagsFilter";
import { UserAdmin } from "./components/pages/admin/UserAdmin";
import { Annotations } from "./components/pages/Annotations";
import { Archive } from "./components/pages/Archive";
import { EReaderView } from "./components/pages/EReaderView";
import { Home } from "./components/pages/Home";
import { ManagementView } from "./components/pages/ManagementView";
import { MyRecentSearches } from "./components/pages/MyRecentSearches";
import { MyShelf } from "./components/pages/MyShelf";
import { NotFoundPage } from "./components/pages/NotFoundPage";
import { Publication } from "./components/pages/Publication";
import { SearchResults } from "./components/pages/SearchResults";
import { trpc } from "./utils/trpc";

import mixpanel from "mixpanel-browser";
import { BookmarkProvider } from "./components/contexts/WithBookmarks/BookmarkProvider.tsx";
import { BookmarksListPage } from "./components/pages/BookmarksListPage.tsx";
import { CollectionsListPage } from "./components/pages/CollectionsListPage.tsx";

function App({
  setAuthError,
  authError,
}: {
  setAuthError: (message: string) => void;
  authError: string;
}) {
  const { apiUrl, mixpanelEnabled } = useConfig();

  useEffect(() => {
    if (authError) {
      setAuthError("");
    }
  }, [setAuthError, authError]);

  useEffect(() => {
    if (mixpanelEnabled) {
      mixpanel.init("62f6215e159a97f9fdd1bc544c50316a", {
        autocapture: true,
        track_pageview: true,
        persistence: "localStorage",
        api_host: "https://api-eu.mixpanel.com",
      });
    }
  }, [mixpanelEnabled]);

  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${apiUrl}/trpc`,
          // You can pass any HTTP headers you wish here
          async headers() {
            const session = await fetchAuthSession();
            return {
              Authorization: session.tokens?.idToken?.toString(),
            };
          },
        }),
      ],
    }),
  );
  return (
    <div style={{ width: "100%" }}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <UserProvider>
              <UserPublicationsProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en-gb"
                >
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <SearchProvider>
                          <ManagementView />
                        </SearchProvider>
                      }
                    >
                      <Route index element={<Home />} />
                      <Route path="my-shelf" element={<MyShelf />} />
                      <Route path="archive" element={<Archive />} />
                      <Route path="annotations" element={<Annotations />} />
                      <Route
                        path="bookmarks"
                        element={<CollectionsListPage />}
                      />
                      <Route
                        path="collections/:collectionId"
                        element={<BookmarksListPage />}
                      />
                      <Route path="frj" element={<FrjArticles />} />
                      <Route path="frj/tags" element={<FrjTagsListPage />} />
                      <Route
                        path="frj/tag/:tagSlug"
                        element={<FrjTagArticlesPage />}
                      />
                      <Route
                        path="frj/issue/:slug"
                        element={<FrjIssuePage />}
                      />
                      <Route
                        path="my-recent-searches"
                        element={<MyRecentSearches />}
                      />
                      <Route path="search" element={<SearchResults />} />
                      <Route path="admin" element={<ProtectedAdminRoute />}>
                        <Route
                          path="organisations"
                          element={<OrganisationAdmin />}
                        />
                        <Route path="users" element={<UserAdmin />} />
                        <Route path="pub" element={<PublicationAdmin />} />
                        <Route
                          path="suggestions"
                          element={<SearchSuggestionsAdmin />}
                        />
                        <Route path="login-page" element={<LoginPageAdmin />} />
                        <Route
                          path="frj-tags-filter"
                          element={<FrjTagsFilterAdmin />}
                        />
                      </Route>
                    </Route>
                    <Route
                      path="pub"
                      element={
                        <PublicationProvider>
                          <AnnotationProvider>
                            <SidebarProvider>
                              <SettingsProvider>
                                <BookmarkProvider>
                                  <EReaderView />
                                </BookmarkProvider>
                              </SettingsProvider>
                            </SidebarProvider>
                          </AnnotationProvider>
                        </PublicationProvider>
                      }
                    >
                      <Route
                        path=":publicationSlug"
                        element={<Publication />}
                      />
                    </Route>
                    <Route
                      path="article"
                      element={
                        <ArticleProvider>
                          <ArticleAnnotationProvider>
                            <SidebarProvider>
                              <SettingsProvider>
                                <ArticleBookmarkProvider>
                                  <ArticleEReaderView />
                                </ArticleBookmarkProvider>
                              </SettingsProvider>
                            </SidebarProvider>
                          </ArticleAnnotationProvider>
                        </ArticleProvider>
                      }
                    >
                      <Route path=":articleSlug" element={<Article />} />
                    </Route>
                    <Route
                      path="*"
                      element={
                        <NotFoundPage message="This page could not be found." />
                      }
                    />
                  </Routes>
                </LocalizationProvider>
              </UserPublicationsProvider>
            </UserProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </div>
  );
}

export type LoginOptions = {
  password: boolean;
  google: boolean;
  microsoft: boolean;
};

const WithErrorHandlingApp = () => {
  const [authError, setAuthError] = useState<string>("");

  useEffect(() => {
    // Check if the URL contains error parameters from Cognito
    const params = new URLSearchParams(window.location.search);
    const errorDescription = params.get("error_description");

    if (errorDescription) {
      // Clean up the Cognito prefix "PreSignUp failed with error "
      const cleanMessage = errorDescription.replace(
        /PreSignUp failed with error |PreAuthentication failed with error /i,
        "",
      );
      setAuthError(cleanMessage);

      // Clean the URL so the error doesn't stay in the address bar
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const [amplifyKey, setAmplifyKey] = useState(0);
  const [loginOptions, setLoginOptions] = useState<LoginOptions | undefined>(
    undefined,
  );

  const resetAmplify = useCallback(() => {
    setLoginOptions(undefined);
    setAmplifyKey((prevKey) => prevKey + 1);
  }, []);

  return (
    <div
      style={{
        height: "100dvh",
      }}
    >
      {authError && (
        <div
          style={{
            color: "white",
            background: "red",
            padding: "10px",
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          ⚠️ {authError}
        </div>
      )}
      <div
        style={{
          height: "100dvh",
        }}
      >
        <Authenticator.Provider key={amplifyKey}>
          <CustomAuthenticator
            resetAmplify={resetAmplify}
            amplifyKey={amplifyKey}
            loginOptions={loginOptions}
            setLoginOptions={setLoginOptions}
            setAuthError={setAuthError}
          >
            <App setAuthError={setAuthError} authError={authError} />
          </CustomAuthenticator>
        </Authenticator.Provider>
      </div>
    </div>
  );
};

export default WithErrorHandlingApp;
